/**
 * 業務詳細（`getBusinessDetail`）の TanStack Query ラッパー。
 *
 * **1 本の問い合わせで master と案件適用後の姿の両方が返る。** 面ごとに 2 回引くと、片方だけが
 * 先に届いた瞬間に「差分なしの画面」が描かれて印が後から入れ替わる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細を master と案件適用後の 2 つの姿として TanStack Query のフックで画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.getBusinessDetail
 */
import type { BusinessDetailPair } from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';

/**
 * 業務 1 件を master と案件適用後の姿で取得する。
 *
 * @param businessId 開く業務の番号
 * @param pjNumber 重ねる案件番号。渡さなければ master だけ
 * @returns 変更前（master）と変更後（案件適用後）の 2 つの姿
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務 1 件を、master と指定した案件を適用した姿の両方として取得する。
 * @graph-connects rpcClient [calls] specGraph.getBusinessDetail
 */
export function useBusinessDetail(
  businessId: number,
  pjNumber?: number,
): UseQueryResult<BusinessDetailPair> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getBusinessDetail(businessId, locale, pjNumber),
    queryFn: () =>
      rpcClient.specGraph.getBusinessDetail({
        businessId,
        ...(locale === undefined ? {} : { locale }),
        ...(pjNumber === undefined ? {} : { pj: { number: pjNumber } }),
      }),
    /* 番号が読めないうちは撃たない（BQ に「id = NaN」を投げない）。 */
    enabled: Number.isInteger(businessId) && businessId > 0,
  });
}
